import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { ToggleLeft, ToggleRight, Save, Power } from 'lucide-react';
import toast from 'react-hot-toast';

const MENU_ITEMS = [
  { key: 'menu_group',   label: '🕌 Group Join',      desc: 'Shehar ka WhatsApp group join karna' },
  { key: 'menu_team',    label: '👥 Join Team',        desc: 'Social media team mein shamil hona' },
  { key: 'menu_program', label: '📅 Program Info',     desc: 'Program details aur requests' },
  { key: 'menu_donate',  label: '💰 Donate Madrasa',   desc: 'Madrasa ke liye donation' },
  { key: 'menu_contact', label: '📞 Contact Us',       desc: 'Admin se rabta karna' },
  { key: 'menu_masail',  label: '📖 Masail Poochein',  desc: 'Islamic masail aur sawal jawab' },
  { key: 'menu_more',    label: '➕ More Services',    desc: 'Books, Videos, Status, Hadees' },
  { key: 'menu_social',  label: '📱 Social Media',     desc: 'YouTube, Facebook, TikTok links' },
  // More sub-items
  { key: 'menu_hadees',  label: '  ↳ Aaj ki Hadees',  desc: 'Daily Hadees image (More ke andar)' },
  { key: 'menu_status',  label: '  ↳ Latest Status',  desc: 'Islamic status images (More ke andar)' },
  { key: 'menu_video',   label: '  ↳ Video Mozo',     desc: 'Topic wise videos (More ke andar)' },
  { key: 'menu_books',   label: '  ↳ Books Download', desc: 'Islamic books (More ke andar)' },
  { key: 'menu_masjid',  label: '  ↳ Masjid Address', desc: 'Masjid location (More ke andar)' },
  { key: 'menu_voice',   label: '  ↳ Voice Masail',   desc: 'Voice note se masail (More ke andar)' },
];

export default function MenuControl() {
  const api = useApi();
  const { can } = useAuth();
  const [menus, setMenus] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const d = await api.get('/menu-control');
    if (d?.menus) {
      // Default sab ON
      const defaults = {};
      MENU_ITEMS.forEach(m => { defaults[m.key] = true; });
      setMenus({ ...defaults, ...d.menus });
    } else {
      const defaults = {};
      MENU_ITEMS.forEach(m => { defaults[m.key] = true; });
      setMenus(defaults);
    }
    setLoading(false);
  }

  function toggle(key) {
    if (!can('settings')) return;
    setMenus(p => ({ ...p, [key]: !p[key] }));
  }

  async function save() {
    setSaving(true);
    const d = await api.post('/menu-control', { updates: menus });
    if (d?.success) toast.success('Menu settings save ho gayi! Bot restart hone par apply hongi.');
    else toast.error(d?.error || 'Error');
    setSaving(false);
  }

  function allOn() { const u = {}; MENU_ITEMS.forEach(m => { u[m.key] = true; }); setMenus(u); }
  function allOff() { const u = {}; MENU_ITEMS.forEach(m => { u[m.key] = false; }); setMenus(u); }

  const onCount = Object.values(menus).filter(Boolean).length;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <span className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="section-title">⚡ Menu Control</div>
          <div className="section-sub">Bot ke main menu items ko on/off karain — jo off hoga wo menu mein nazar nahi aayega</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12,
            background: 'var(--emerald-900)', border: '1px solid var(--emerald-700)',
            color: 'var(--emerald-300)'
          }}>
            {onCount}/{MENU_ITEMS.length} Active
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={allOn} disabled={!can('settings')}>
          ✅ Sab On
        </button>
        <button className="btn btn-ghost btn-sm" onClick={allOff} disabled={!can('settings')}>
          ❌ Sab Off
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {MENU_ITEMS.map((item, i) => {
          const isOn = menus[item.key] !== false;
          const isSubItem = item.label.startsWith('  ↳');
          return (
            <div
              key={item.key}
              onClick={() => toggle(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: isSubItem ? '12px 20px 12px 36px' : '14px 20px',
                borderBottom: i < MENU_ITEMS.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: can('settings') ? 'pointer' : 'default',
                background: isOn ? 'transparent' : 'rgba(224,82,82,0.04)',
                transition: 'background 0.15s',
              }}
            >
              {/* Toggle icon */}
              <div style={{
                color: isOn ? 'var(--emerald-400)' : 'var(--text-muted)',
                transition: 'color 0.2s', flexShrink: 0
              }}>
                {isOn
                  ? <ToggleRight size={28} />
                  : <ToggleLeft size={28} />
                }
              </div>

              {/* Label */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500,
                  color: isOn ? 'var(--text-primary)' : 'var(--text-muted)',
                  transition: 'color 0.2s'
                }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                  {item.desc}
                </div>
              </div>

              {/* Status badge */}
              <div style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: isOn ? 'var(--emerald-900)' : 'var(--danger-bg)',
                color: isOn ? 'var(--emerald-300)' : 'var(--danger)',
                border: `1px solid ${isOn ? 'var(--emerald-700)' : '#5a1a1a'}`,
                transition: 'all 0.2s', minWidth: 42, textAlign: 'center'
              }}>
                {isOn ? 'ON' : 'OFF'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div style={{
        marginTop: 12, padding: '10px 14px',
        background: 'var(--gold-900)', border: '1px solid var(--gold-700)',
        borderRadius: 8, fontSize: 12, color: 'var(--gold-200)'
      }}>
        ⚠️ Note: Jo menu item off hoga wo bot ke menu list mein nazar nahi aayega. Bot server restart hone ke baad changes apply honge.
      </div>

      {can('settings') && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Save size={14} />}
            {saving ? ' Saving...' : ' Changes Save Karein'}
          </button>
        </div>
      )}
    </div>
  );
}